import { z } from 'zod'

import {
  automationSchema,
  createAutomationResponseSchema,
  createAutomationRequestSchema,
  updateAutomationRequestSchema,
  trashedAutomationItemSchema,
  trashedAutomationPageSchema,
  type Automation,
  type CreateAutomationRequest,
  type UpdateAutomationRequest,
  type TrashedAutomationItem,
  type TrashedAutomationPage,
} from '@/types/api/automation'
import { trashStatsSchema, type TrashStats } from '@/types/api/trash'

import { apiClient } from './client'


export async function getAutomations(): Promise<Automation[]> {
  const response = await apiClient.get('/automations')
  return z.array(automationSchema).parse(response.data)
}

export async function getAutomationsForRecordGroup(recordGroupId: string): Promise<Automation[]> {
  const response = await apiClient.get(`/automations/record-group/${recordGroupId}`)
  return z.array(automationSchema).parse(response.data)
}

export async function getAutomation(automationId: string): Promise<Automation> {
  const response = await apiClient.get(`/automations/${automationId}`)
  return automationSchema.parse(response.data)
}

export async function createAutomation(data: CreateAutomationRequest): Promise<Automation> {
  const validated = createAutomationRequestSchema.parse(data)
  const response = await apiClient.post('/automations', validated)
  // POST returns DTOAutomationCreationResponse (no actions/timestamps)
  return createAutomationResponseSchema.parse(response.data) as Automation
}

export async function updateAutomation(
  automationId: string,
  data: UpdateAutomationRequest
): Promise<Automation> {
  const validated = updateAutomationRequestSchema.parse(data)
  const response = await apiClient.put(`/automations/${automationId}`, validated)
  return automationSchema.parse(response.data)
}

export async function enableAutomation(automationId: string): Promise<void> {
  await apiClient.patch(`/automations/${automationId}/enable`)
}

export async function disableAutomation(automationId: string): Promise<void> {
  await apiClient.patch(`/automations/${automationId}/disable`)
}

export async function deleteAutomation(automationId: string): Promise<void> {
  await apiClient.delete(`/automations/${automationId}`)
}

export async function getTrashedAutomations(pageNumber: number, pageSize: number): Promise<TrashedAutomationPage> {
  const response = await apiClient.get(`/automations/trash`, {
    params: { page: pageNumber, size: pageSize },
  })
  return trashedAutomationPageSchema.parse(response.data)
}

export async function getAllTrashedAutomations(): Promise<TrashedAutomationItem[]> {
  const response = await apiClient.get('/automations/trash/all')
  return z.array(trashedAutomationItemSchema).parse(response.data)
}

export async function searchTrashedAutomationsByRecordGroup(
  recordGroupId: string,
  pageNumber: number,
  pageSize: number
): Promise<TrashedAutomationPage> {
  const response = await apiClient.get(`/automations/trash/record-group/${recordGroupId}`, {
    params: { page: pageNumber, size: pageSize },
  })
  return trashedAutomationPageSchema.parse(response.data)
}

export async function getAutomationTrashStats(): Promise<TrashStats> {
  const response = await apiClient.get('/automations/trash/stats')
  return trashStatsSchema.parse(response.data)
}

export async function restoreAutomation(automationId: string): Promise<TrashedAutomationItem> {
  const response = await apiClient.post(`/automations/trash/${automationId}/restore`, undefined, {
    suppressErrorToast: true,
  })
  return trashedAutomationItemSchema.parse(response.data)
}

export async function permanentlyDeleteAutomation(automationId: string): Promise<void> {
  await apiClient.delete(`/automations/trash/${automationId}`, {
    suppressErrorToast: true,
  })
}